import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  getAssetPath,
  JSX
} from "@stencil/core";
import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { Locale } from "../../common/locale";

import type { PackageData, License, OpenPackageURLCallback } from "./types";

const OPEN_WINDOW_ICON = getIconPath({
  category: "gemini-tools",
  name: "open-window",
  colorType: "primary"
});

const IMAGES_PATH = "./gx-ide-assets/details-acknowledgements/images";
const BACKGROUND_SIZE = "300px";

const GENEXUS_NEXT_LOGO_SRC = getAssetPath(
  `${IMAGES_PATH}/genexus-next-logo.svg`
);
const BG_IMAGE_TOP_START = getAssetPath(`${IMAGES_PATH}/top-start.svg`);
const BG_IMAGE_BOTTOM_START = getAssetPath(`${IMAGES_PATH}/bottom-start.svg`);
const BG_IMAGE_BOTTOM_END = getAssetPath(`${IMAGES_PATH}/bottom-end.svg`);

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "utils/elevation",
  "components/button",
  "components/icon",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-details-acknowledgements",
  styleUrl: "details-acknowledgements.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/details-acknowledgements"]
})
export class GxIdeDetailsAcknowledgements {
  /**
   * The component hard-coded strings translations.
   */
  _componentLocale: any;

  @Element() el: HTMLGxIdeDetailsAcknowledgementsElement;

  /**
   * Identifier of the currently expanded license. Should combine:
   * packageName + packageVersion + license.name. Example:
   * @sentry/core-6.7.1-mit
   */
  @State() expandedLicenseId?: string;

  /** The date when the current release was published */
  @Prop() readonly dateGenerated?: Date;

  /** The version of the current release*/
  @Prop() readonly release?: string;

  /** The list of packages that the current release contains*/
  @Prop() readonly packages: PackageData[];

  /**
   * Callback invoked when the user clicks a package with a packageURL.
   * If openPackageUrlCallback is defined, the default link action is
   * prevented and the URL is passed to the host via the callback.
   * Otherwise, the link opens in a new tab as usual.
   */
  @Prop() readonly openPackageUrlCallback: OpenPackageURLCallback;

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  #getFormatedDate = (date: Date) => {
    return date?.toISOString().split("T")[0];
  };

  #renderLicenseInfo = (
    license: License,
    type: "name" | "description" = "name",
    packageData: PackageData
  ): JSX.Element => {
    const licenseId = this.#getLicenseId(packageData, license.name);
    const showDescription = licenseId === this.expandedLicenseId;
    return type === "name" ? (
      <ul class="licenses-names-list">
        <li class="body-regular-m licenses-names-item">{license.name}</li>
      </ul>
    ) : (
      <ul class="licenses-descriptions-list">
        <li class="licenses-descriptions-item" key={license.name}>
          {showDescription ? (
            <p class="code-box color-neutral licenses-descriptions-description scrollable">
              {license.description}
            </p>
          ) : null}
        </li>
      </ul>
    );
  };

  #renderLicensesViewButtons = (
    license: License,
    packageData?: PackageData
  ): JSX.Element => {
    const licenseId = this.#getLicenseId(packageData, license.name);
    const visibleLicenseButton = licenseId === this.expandedLicenseId;
    const viewLicenseButtonLabel = visibleLicenseButton
      ? this._componentLocale.detailsCard.hide
      : this._componentLocale.detailsCard.show;
    return (
      <div class="licenses-buttons-container buttons-spacer">
        {
          <button
            class={{
              "button-icon-and-text": true,
              "button-primary": !visibleLicenseButton,
              "button-secondary": visibleLicenseButton,
              "show-more-button": true
            }}
            onClick={this.#viewLicenseClickedHandler(licenseId)}
          >
            {viewLicenseButtonLabel}
          </button>
        }
      </div>
    );
  };

  #getLicenseId = (packageData: PackageData, licenseName: string): string => {
    return `${packageData.packageName}-${packageData.packageVersion}-${licenseName}`;
  };

  #viewLicenseClickedHandler = (licenseId: string) => () => {
    if (licenseId === this.expandedLicenseId) {
      // collapse
      this.expandedLicenseId = undefined;
    } else {
      // expand collapse
      this.expandedLicenseId = licenseId;
    }
  };

  #openLinkClickedHandler = (url: string) => (e: MouseEvent) => {
    if (this.openPackageUrlCallback) {
      e.preventDefault();
      this.openPackageUrlCallback(url);
    }
  };

  render() {
    const formatedDate = this.#getFormatedDate(this.dateGenerated);
    return (
      <Host
        class="scrollable"
        style={{
          backgroundImage: `url(${BG_IMAGE_TOP_START}), url(${BG_IMAGE_BOTTOM_START}), url(${BG_IMAGE_BOTTOM_END})`,
          backgroundPosition: "left top, left bottom, right bottom",
          backgroundRepeat: "no-repeat, no-repeat, no-repeat",
          backgroundSize: `${BACKGROUND_SIZE}, ${BACKGROUND_SIZE}, ${BACKGROUND_SIZE}`
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <div class="main-wrapper">
          <div
            style={{
              mask: `url(${GENEXUS_NEXT_LOGO_SRC}) no-repeat center`
            }}
            class="logo"
          ></div>
          <div class="card card-details elevation-1">
            <div class="intro-container">
              <h1 class="title heading-3">
                {this._componentLocale.detailsCard.title}
              </h1>
              <p class="body-regular-m">
                {this._componentLocale.detailsCard.mainDescription}
              </p>
              <p class="color-neutral body-italic-s">
                {this._componentLocale.detailsCard.secondaryDescription}
              </p>
            </div>

            <hr />

            <div class="date-container">
              <p class="color-neutral">
                {this._componentLocale.detailsCard.dateGenerated}
              </p>
              <time dateTime={formatedDate}>{formatedDate}</time>
            </div>
          </div>

          {this.packages?.map(packageItem => {
            let packageName = `${packageItem.packageName} ${packageItem.packageVersion}`;
            const headingContent = packageItem.packageURL ? (
              <a
                href={packageItem.packageURL}
                target="_blank"
                onClick={this.#openLinkClickedHandler(packageItem.packageURL)}
                class="package-link"
              >
                {packageName}
                <ch-image
                  src={OPEN_WINDOW_ICON}
                  class="package-icon icon-s"
                ></ch-image>
              </a>
            ) : (
              packageName
            );

            return (
              <article class="card card-package elevation-1">
                <h2
                  class={{
                    "subtitle-semi-bold-s": true,
                    "package-name": true,
                    "has-link": !!packageItem.packageURL
                  }}
                >
                  {headingContent}
                </h2>
                <p class="subtitle-semi-bold-s">
                  {this._componentLocale.packageCard.declaredLicenses}
                </p>
                {this.#renderLicenseInfo(
                  packageItem.declaredLicense,
                  "name",
                  packageItem
                )}

                {this.#renderLicenseInfo(
                  packageItem.declaredLicense,
                  "description",
                  packageItem
                )}

                {this.#renderLicensesViewButtons(
                  packageItem.declaredLicense,
                  packageItem
                )}
              </article>
            );
          })}
        </div>
      </Host>
    );
  }
}
